import React from 'react';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { cn, formatCurrency, formatDate } from '../../lib/utils';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';

const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'CH';

export const ChildrenTable = ({ children, canEdit, onAddFunds, onEdit, onDelete }) => (
  <Card>
    <CardHeader>
      <CardTitle className="font-heading">All Sponsored Children</CardTitle>
      <CardDescription>{children.length} children registered</CardDescription>
    </CardHeader>
    <CardContent className="p-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Grade / School</TableHead>
            <TableHead>Sponsor</TableHead>
            <TableHead className="text-right">Balance</TableHead>
            <TableHead className="text-right">Spent</TableHead>
            {canEdit && <TableHead className="w-48"></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {children.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                No children registered yet
              </TableCell>
            </TableRow>
          ) : (
            children.map((child) => (
              <TableRow key={child.id} className="table-row-hover" data-testid={`child-row-${child.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="bg-pink-100 text-pink-600 text-xs">
                        {getInitials(child.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{child.name}</p>
                      {child.date_of_birth && <p className="text-xs text-muted-foreground">DOB: {formatDate(child.date_of_birth)}</p>}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <p className="text-sm">{child.grade || '—'}</p>
                    <p className="text-xs text-muted-foreground">{child.school || '—'}</p>
                  </div>
                </TableCell>
                <TableCell className="text-sm">{child.sponsor_name || '—'}</TableCell>
                <TableCell className={cn("text-right font-medium", child.account_balance >= 0 ? "text-green-600" : "text-red-600")}>
                  {formatCurrency(child.account_balance)}
                </TableCell>
                <TableCell className="text-right text-red-600">{formatCurrency(child.total_expenses)}</TableCell>
                {canEdit && (
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => onAddFunds(child)} data-testid={`funds-${child.id}`} className="h-8 px-2.5">
                        <Plus className="h-3.5 w-3.5 mr-1" /> Funds
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => onEdit(child)} data-testid={`edit-child-${child.id}`} className="h-8 px-2.5">
                        <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => onDelete(child)} data-testid={`delete-child-${child.id}`} className="h-8 px-2.5 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive">
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

export const ChildExpensesTable = ({ expenses, canEdit, onEdit, onDelete }) => (
  <Card>
    <CardContent className="p-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Child</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Description</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            {canEdit && <TableHead className="w-44"></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {expenses.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                No expenses recorded yet
              </TableCell>
            </TableRow>
          ) : (
            expenses.map((expense) => (
              <TableRow key={expense.id} className="table-row-hover">
                <TableCell>{formatDate(expense.date)}</TableCell>
                <TableCell className="font-medium">{expense.child_name}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="capitalize">
                    {expense.custom_type_name || expense.expense_type.replace('_', ' ')}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">{expense.description || '-'}</TableCell>
                <TableCell className="text-right font-medium text-red-600">
                  {formatCurrency(expense.amount)}
                </TableCell>
                {canEdit && (
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => onEdit(expense)} data-testid={`edit-child-expense-${expense.id}`} className="h-8 px-2.5">
                        <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => onDelete(expense)} data-testid={`delete-child-expense-${expense.id}`} className="h-8 px-2.5 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive">
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);
